'use client';
import ProgressBar from '@koda/koda-components/molecules/ProgressBar';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { MainLocale } from '../../../../i18n/constants';
import { useCurrentStepRoute } from '../components/useCurrentStepRoute';
import { FlowController } from './FlowController';
import { useProgressValidator } from './hooks/useProgressValidator';
import type { SanityLicense } from './types/licenses';

export const LicenseProgressBar = ({ license }: { license: SanityLicense }) => {
  const currentStepRoute = useCurrentStepRoute();
  const locale = useLocale();
  const router = useRouter();
  const { maxAccessibleProgressStepNumber } = useProgressValidator(license);
  if (!license || !currentStepRoute) {
    return null; // Missing context; nothing to render.
  }

  const flowController = new FlowController(license);
  const progress = flowController.getProgressFromSlug(currentStepRoute);

  const handleProgressClick = (index: number) => {
    const currentStepNumber = progress?.currentStepNumber ?? 0;
    if (currentStepNumber <= 0) {
      return;
    }

    // Allow clicking backwards freely, and forwards only up to the first invalid (unlocked) progress step.
    if (index > maxAccessibleProgressStepNumber) {
      return;
    }
    const route = flowController.getAbsoluteRouteInDirection({
      currentStepRoute,
      direction: index - currentStepNumber,
      locale: locale as MainLocale,
    });
    if (route) router.push(route);
  };

  return (
    <div>
      <ProgressBar
        onProgressClick={handleProgressClick}
        maxClickableStep={maxAccessibleProgressStepNumber}
        length={progress?.totalSteps ? progress.totalSteps : 0}
        current={progress?.currentStepNumber ? progress.currentStepNumber : 0}
        className="justify-start"
      />
    </div>
  );
};
